import { Feather } from "@expo/vector-icons";
import { useRef, useState } from "react";
import { Keyboard, Platform, Pressable, TextInput as RNTextInput, View } from "react-native";
import { MaskedTextInput } from "react-native-mask-text";
import { Text, TouchableRipple } from "react-native-paper";
import { colors } from "../../../../theme";
import { ActionsContainer, Button, KeyboardAvoidingView, Modal, ScrollableContainer, TextInput } from "../../../components";
import Bank from "../../../svgs/Bank";
import CreditCard from "../../../svgs/CreditCard";
import { formatCurrency, openBrowser } from "../../utils";
import { AddonType, MutationInput, OrderType, PDF_QUERY, PaymentMethod, PdfQuery } from "./types";
import Divider from "../Divider";
import { InsuranceModal, ContributionsModal } from "./Modals";
import Hyphen from "../../../svgs/Hyphen";
import Checkmark from "../../../svgs/Checkmark";
import MoneyCheck from "../../../svgs/MoneyCheck";
import { useLazyQuery } from "@apollo/client";

export const OrderForm = ({
  order,
  openCheckout,
  isProxy,
}: {
  order: OrderType;
  openCheckout: (input: MutationInput) => Promise<any>;
  isProxy?: boolean;
}) => {
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
  const [showOtherPaymentMethods, setShowOtherPaymentMethods] = useState(false);
  const [pressedContinue, setPressedContinue] = useState(false);
  const [addons, setAddons] = useState<AddonType[]>(order.addons.filter((item) => item.changeablePrice));

  const lineItems = order.lineItems;
  const lineItemsSubtotal = lineItems.reduce((acc, lineItem) => acc + lineItem.balance, 0);
  const lineItemsTotalPrice = lineItems.reduce((acc, lineItem) => acc + lineItem.totalPrice, 0);
  const partialPaymentCredit = lineItemsTotalPrice - lineItemsSubtotal;
  const addonsSubtotal = addons.reduce((acc, lineItem) => acc + lineItem.priceCents, 0);
  const duesTotal = lineItemsSubtotal + addonsSubtotal;

  const [showInsuranceModal, setShowInsuranceModal] = useState<boolean>(false);
  const [showContributionsModal, setShowContributionsModal] = useState<boolean>(false);

  async function onContinuePayment(selectedOption: PaymentMethod) {
    setLoading(true);
    await openCheckout({
      orderId: order.id,
      paymentMethod: selectedOption,
      addons: addons.map((item) => ({
        productVariantLookupKey: item.productVariantLookupKey,
        priceCents: item.priceCents,
      })),
    });
    setLoading(false);
  }

  return (
    <KeyboardAvoidingView>
      <ScrollableContainer keyboardShouldPersistTaps="handled">
        <View className="p-6">
          <Text className="text-large-normal font-700">Membership Dues</Text>
          <Text className="text-small-normal font-400 my-2">
            Insurance and the Member Assistance Program is a requirement of membership.{" "}
            <Text onPress={() => setShowInsuranceModal(true)} className="underline text-primary">
              Learn More
            </Text>
          </Text>
          {lineItems.map((lineItem, i) => (
            <LineItem
              key={lineItem.id}
              containerClass="my-3"
              name={lineItem.name}
              amountCents={lineItem.totalPrice}
              testID={`line-item-${i}`}
            />
          ))}
          {partialPaymentCredit > 0 && (
            <LineItem
              containerClass="my-3"
              name="Partial Payment Credit"
              amountCents={-partialPaymentCredit}
              testID="line-item-partial-payment-credit"
            />
          )}
          <Subtotal name="Dues Subtotal" containerClass="mt-6" amountCents={lineItemsSubtotal} testID="line-items-subtotal" />
        </View>
        <Divider />
        <View className="p-6">
          <Text className="text-large-normal font-700">Voluntary Contributions</Text>
          <Text className="text-small-normal font-400 my-2">
            Contributing to assistance funds* is voluntary. Dues, fees, assessments, contributions or gifts to the PGA of
            America are not tax deductible as charitable contributions.{" "}
            <Text onPress={() => setShowContributionsModal(true)} className="underline text-primary">
              Learn More
            </Text>
          </Text>
          {addons.map((lineItem, i) => (
            <View key={lineItem.productVariantLookupKey} testID={`addon-${i}`} className="flex flex-row my-3 justify-between">
              <Text className="text-regular-tight text-ink-dark shrink mr-8">{lineItem.name}</Text>
              <AddonInput
                value={`${Math.floor(lineItem.priceCents / 100)}`}
                index={i}
                onChange={(price) => {
                  const lineItems = [...addons];
                  lineItems[i] = { ...lineItems[i], priceCents: price * 100 };
                  setAddons(lineItems);
                }}
              />
            </View>
          ))}
          <Subtotal name="Contributions Subtotal" containerClass="mt-6" amountCents={addonsSubtotal} testID="addons-subtotal" />
        </View>
        <Divider />

        <View className="p-6">
          <Text className="text-large-normal font-700">Select Payment Option</Text>
          {pressedContinue && !paymentMethod && <Text className="text-red-dark font-400 mt-2">Payment option is required</Text>}

          <PaymentOptionSelector
            onOtherPaymentMethods={() => {
              Keyboard.dismiss();
              setPressedContinue(false);
              setPaymentMethod(undefined);
              setShowOtherPaymentMethods(true);
            }}
            duesTotal={duesTotal}
            setValue={(value) => {
              Keyboard.dismiss();
              setPressedContinue(false);
              setPaymentMethod(value);
            }}
            currentValue={paymentMethod}
          />
        </View>

        <ActionsContainer>
          <Button
            testID="continue-to-payment-button"
            mode="contained"
            disabled={loading}
            isValid={!!paymentMethod}
            loading={loading}
            onPress={async () => {
              Keyboard.dismiss();
              setPressedContinue(true);

              if (paymentMethod) {
                await onContinuePayment(paymentMethod);
                setPressedContinue(false);
              }
            }}
          >
            Continue to Payment
          </Button>
        </ActionsContainer>
        <InsuranceModal onDismiss={() => setShowInsuranceModal(false)} visible={showInsuranceModal} />
        <ContributionsModal onDismiss={() => setShowContributionsModal(false)} visible={showContributionsModal} />
      </ScrollableContainer>
      <OtherPaymentsModal
        visible={showOtherPaymentMethods}
        onDismiss={() => setShowOtherPaymentMethods(false)}
        duesTotal={duesTotal}
        order={order}
        isProxy={isProxy}
        onContinuePayment={onContinuePayment}
      />
    </KeyboardAvoidingView>
  );
};

const LineItem = ({
  name,
  amountCents,
  containerClass,
  testID,
}: {
  containerClass: string;
  name: string;
  amountCents: number;
  testID?: string;
}) => {
  const amountColor = amountCents < 0 ? "text-green" : "text-ink-darkest";
  return (
    <View className={`flex flex-row justify-between ${containerClass}`} testID={testID}>
      <Text className="text-regular-tight text-ink-dark shrink mr-20">{name}</Text>
      <Text className={`text-regular-tight ${amountColor}`}>{formatCurrency({ value: amountCents / 100, unit: "USD" })}</Text>
    </View>
  );
};

const Subtotal = ({
  name,
  amountCents,
  containerClass,
  testID,
}: {
  containerClass: string;
  name: string;
  amountCents: number;
  testID?: string;
}) => {
  return (
    <View className={`flex flex-row justify-between ${containerClass}`} testID={testID}>
      <Text className="text-regular-tight font-700">{name}</Text>
      <Text className="text-regular-tight font-700">{formatCurrency({ value: amountCents / 100, unit: "USD" })}</Text>
    </View>
  );
};

const PaymentOptionSelector = ({
  currentValue,
  setValue,
  duesTotal,
  onOtherPaymentMethods,
}: {
  currentValue?: PaymentMethod;
  setValue: (value?: PaymentMethod) => void;
  duesTotal: number;
  onOtherPaymentMethods(): void;
}) => {
  return (
    <View className="flex pt-4 gap-4">
      <TouchableRipple
        testID={`payment-option-bank`}
        onPress={() => setValue(PaymentMethod.Bank)}
        className={`rounded-lg p-4 ${
          currentValue === PaymentMethod.Bank ? "border-[2px] border-primary-lighter" : "border-[1px] border-sky-dark"
        }`}
      >
        <View>
          <View className="flex flex-row items-center">
            <Bank />
            <Text className="text-regular-tight font-700 ml-2">Bank Account</Text>
            <Text className="text-large-tight font-700 ml-auto">{formatCurrency({ value: duesTotal / 100, unit: "USD" })}</Text>
          </View>

          {[
            { text: "Credentials will not be sent until ACH payment finalizes", Icon: Hyphen, bold: false },
            { text: "Longer processing time (up to 5 days)", Icon: Hyphen, bold: false },
            { text: "0 Fees", Icon: Checkmark, bold: true },
          ].map((item, i) => {
            return (
              <View key={i} className="flex flex-row mt-2">
                <item.Icon />
                <Text className={`text-tiny-normal flex-1 text-ink-base ml-1 ${item.bold ? "font-700" : "font-400"}`}>
                  {item.text}
                </Text>
              </View>
            );
          })}
        </View>
      </TouchableRipple>
      <TouchableRipple
        testID="other-payment-methods-button"
        onPress={onOtherPaymentMethods}
        className={`py-4 rounded-lg px-4 border-[1px] border-sky-dark`}
      >
        <View className="flex flex-row items-center justify-between">
          <Text className="text-regular-tight font-400">Other ways to pay</Text>
          <Feather name="chevron-right" size={30} />
        </View>
      </TouchableRipple>
    </View>
  );
};

const OtherPaymentsModal = ({
  visible,
  onDismiss,
  duesTotal,
  order,
  isProxy,
  onContinuePayment,
}: {
  visible: boolean;
  onDismiss: () => void;
  duesTotal: number;
  order: OrderType;
  isProxy?: boolean;
  onContinuePayment: (selectedOption: PaymentMethod) => Promise<void>;
}) => {
  const creditCardFee = order.creditCardFee;
  const creditCardFeeAmount = Math.ceil(duesTotal * creditCardFee);
  const creditCardTotal = Math.ceil(duesTotal + creditCardFeeAmount);
  const showPrintInvoice = !isProxy;

  return (
    <Modal visible={visible} fullScreen title="Other Payment Options" onDismiss={onDismiss}>
      <View className={`p-6 ${showPrintInvoice ? "border-b border-b-sky-light" : ""}`}>
        <View className="flex flex-row items-center">
          <CreditCard />
          <Text className="text-large-tight font-700 ml-2">Credit or Debit Card</Text>
        </View>
        <Text className="text-regular-tight font-400 mt-4 mb-2">
          Pay with credit or debit for an added {creditCardFee * 100}% convenience fee and receive:
        </Text>

        {["Immediate renewal", "Uninterrupted benefits"].map((text, i) => {
          return (
            <View key={i} className="flex flex-row items-center mt-2">
              <Checkmark height={24} />
              <Text className="text-regular-tight text-ink-base font-400 ml-1">{text}</Text>
            </View>
          );
        })}

        <LineItem name="Dues Total" containerClass="mt-6" amountCents={duesTotal} />
        <LineItem name={`${creditCardFee * 100}% Convenience Fee`} containerClass="mt-2" amountCents={creditCardFeeAmount} />

        <Subtotal name="Total Due" amountCents={creditCardTotal} containerClass="mt-2" />

        <Button
          mode="contained"
          className="mt-6"
          testID="payment-option-card"
          onPress={() => {
            onContinuePayment(PaymentMethod.Card);
            onDismiss();
          }}
        >
          Pay {formatCurrency({ value: creditCardTotal / 100, unit: "USD" })} with Card
        </Button>
      </View>
      {showPrintInvoice && (
        <View className="p-6">
          <View className="flex flex-row items-center">
            <MoneyCheck />
            <Text className="text-large-tight font-700 ml-2">By Mail with Check</Text>
          </View>
          <Text className="text-regular-tight font-400 mt-4 mb-2">Pay by check for 0 fees.</Text>

          {[
            "Credentials will not be sent until check payment finalizes",
            "Longer processing time (up to 5 days) not including delivery time",
          ].map((text, i) => {
            return (
              <View key={i} className="flex flex-row mt-2">
                <Hyphen height={24} className="shrink-0" />
                <Text className="text-regular-tight flex-1 text-ink-base font-400 ml-1">{text}</Text>
              </View>
            );
          })}

          <Text className="text-regular-tight font-400 mt-4 mb-8">Print & include the invoice with your check.</Text>

          <PrintInvoiceButton order={order} onDismiss={onDismiss} />
        </View>
      )}
      <Button mode="text" className="px-6 mb-12" onPress={onDismiss} testID="other-payment-methods-cancel">
        Cancel
      </Button>
    </Modal>
  );
};

const removeTabIndex = (ref: any) => {
  if (Platform.OS === "web") {
    ref?.removeAttribute("tabindex");
  }
};

const PrintInvoiceButton = ({ onDismiss }: { order: OrderType; onDismiss: () => void }) => {
  const [pdfQuery, { loading }] = useLazyQuery<PdfQuery>(PDF_QUERY);

  const handlePress = async () => {
    const { data } = await pdfQuery();
    const pdfUrl = data?.duesCycle?.order?.pdfUrl;
    onDismiss();
    pdfUrl && (await openBrowser(pdfUrl)());
  };

  return (
    <Button testID="payment-option-print" mode="contained" loading={loading} disabled={loading} onPress={handlePress}>
      Print Invoice
    </Button>
  );
};

const AddonInput = ({ value, index, onChange }: { index: number; value?: string; onChange: (value: number) => void }) => {
  const [focused, setFocused] = useState(false);
  const focusRef = useRef<RNTextInput>(null);

  function focusInput() {
    if (focusRef.current) focusRef.current.focus();
  }

  const closeButtonId = `addon-input-button-${value}`;

  return (
    <Pressable onPress={focusInput} ref={removeTabIndex} className="web:shrink web:max-w-[150px] web:min-w-[120px]">
      <TextInput
        value={value}
        outlineStyle={focused && { borderColor: colors.primary.DEFAULT, borderWidth: 2 }}
        render={({ onChangeText, ...props }) => (
          <View className="flex flex-row px-3 items-center">
            <View className="pl-1 pt-2 shrink">
              <Text className="text-tiny font-400 text-ink-lighter">Amount</Text>
              <View className="flex flex-row items-center">
                <Text className="text-regular font-400 text-ink-darkest ios:pb-1">$</Text>
                <MaskedTextInput
                  value={props.value}
                  type="currency"
                  selectTextOnFocus
                  testID={`addon-input-${index}`}
                  ref={focusRef}
                  onFocus={() => setFocused(true)}
                  onBlur={(e: any) => {
                    // On web, we need to check if the relatedTarget is the close button to not set focused to false
                    if (Platform.OS !== "web" || e?.relatedTarget?.id !== closeButtonId) {
                      setFocused(false);
                    }
                  }}
                  className="text-regular font-400 text-ink-darkest h-[30px] min-w-[30px]"
                  options={{
                    decimalSeparator: ".",
                    groupSeparator: ",",
                    precision: 0,
                  }}
                  onChangeText={(_text, rawText) => {
                    onChange(parseInt(rawText) || 0);
                  }}
                  keyboardType="numeric"
                />
              </View>
            </View>

            <View className="w-10 items-center">
              {focused && (
                <TouchableRipple
                  id={closeButtonId}
                  onPress={() => {
                    setFocused(false);
                    Keyboard.dismiss();
                    onChange(0);
                  }}
                  onBlur={() => setFocused(false)}
                  className="ml-2 bg-sky-lighter rounded-full p-1 overflow-hidden"
                >
                  <Feather name="x" size={20} />
                </TouchableRipple>
              )}
            </View>
          </View>
        )}
      />
    </Pressable>
  );
};
